import { Drawer as DrawerBase, type DrawerProps } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { DrawerContext } from "@/components/Drawer/DrawerContext";
import { useIsVirtualKeyboardOpen } from "@/hooks/useIsVirtualKeyboardOpen";

type Props = Pick<DrawerProps, "open" | "disableRestoreFocus" | "children"> & {
  onClose?: () => void;
  hasFullHeight?: boolean;
  heightOffset?: number;
  backgroundColor?: "white" | "background";
};

export function Drawer({
  open,
  children,
  onClose,
  hasFullHeight,
  disableRestoreFocus,
  backgroundColor = "white",
  heightOffset = 40,
}: Props) {
  const [isScrolled, setIsScrolled] = useState(false);

  const contentWrapperRef = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;

    setIsScrolled(element.scrollTop > 0);
    const handleScroll = () => setIsScrolled(element.scrollTop > 0);
    element.addEventListener("scroll", handleScroll);

    return () => element.removeEventListener("scroll", handleScroll);
  }, []);

  const heightOffsetWithUnit = `${heightOffset}px`;

  const heightStyle = hasFullHeight
    ? `calc(100dvh - ${heightOffsetWithUnit})`
    : "auto";

  const { isVirtualKeyboardOpen, virtualKeyboardHeight } =
    useIsVirtualKeyboardOpen();

  const contextValue = useMemo(
    () => ({
      isSwipeable: false,
      isScrolled,
      backgroundColor,
    }),
    [isScrolled, backgroundColor],
  );

  return (
    <DrawerBase
      anchor="bottom"
      open={open}
      onClose={(e, reason) => {
        // do not allow to close the drawer on backdrop click
        if (reason === "backdropClick") return;
        onClose?.();
      }}
      disableRestoreFocus={disableRestoreFocus}
      sx={{
        "& > .MuiDrawer-paper": {
          height: heightStyle,
          backgroundColor:
            backgroundColor === "white"
              ? "var(--color-white)"
              : "var(--color-surface-background)",
          paddingTop: 0,
          px: 0,
          ...(isVirtualKeyboardOpen
            ? { paddingBottom: `${virtualKeyboardHeight}px` }
            : {}),
        },
      }}
    >
      <DrawerContext.Provider value={contextValue}>
        <div
          ref={contentWrapperRef}
          className="flex grow-1 flex-col overflow-y-auto overscroll-x-none"
        >
          {children}
        </div>
      </DrawerContext.Provider>
    </DrawerBase>
  );
}
